<script setup lang="ts">
import { useForm, usePage, Link } from "@inertiajs/vue3"
import { computed, watch } from "vue"
import route from "ziggy-js"
import { CalendarDays, Megaphone } from "lucide-vue-next"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { useToast } from "@/components/ui/toast/use-toast"
import AppLayout from "@/layouts/AppLayout.vue"

defineOptions({
    layout: [AppLayout],
})

type Employee = {
    id: number
    employee_number: string
    name: string
    title: string
}

type Shift = {
    id: number
    station: string
    employee: Employee
    start_label: string
    end_label: string
    login_status: "on_time" | "late" | "pending"
}

type Attendance = {
    status: "timed_in" | "timed_out" | "none"
    timed_in_label: string | null
    timed_out_label: string | null
}

type Announcement = {
    id: number
    title: string
    body: string
    author: string | null
    published_label: string | null
}

type Props = {
    dateLabel: string
    employee: Employee | null
    myShift: Shift | null
    attendance: Attendance | null
    schedule: Shift[]
    announcements: Announcement[]
    canPostAnnouncements: boolean
}

const props = defineProps<Props>()
const page = usePage()
const { toast } = useToast()
const links = computed(() => page.props.inertiaLinks as { time_clock_path: string; announcements_store_path: string })

const announcementForm = useForm({
    title: "",
    body: "",
})

watch(
    () => (page.props.flash as { status?: string } | undefined)?.status,
    (status) => {
        if (status) {
            toast({ description: status })
        }
    },
    { immediate: true }
)

const stationLabel: Record<string, string> = {
    barista: "Barista",
    cashier: "Cashier",
    kitchen: "Kitchen",
}

const groupedSchedule = computed(() => {
    const groups = ["barista", "cashier", "kitchen"].map((station) => ({
        station,
        label: stationLabel[station] ?? station,
        shifts: props.schedule.filter((shift) => shift.station === station),
    }))

    const extras = props.schedule.filter((shift) => !stationLabel[shift.station])

    if (extras.length) {
        groups.push({ station: "other", label: "Other", shifts: extras })
    }

    return groups
})

function submitAnnouncement() {
    announcementForm.post(route("announcements.store"), {
        preserveScroll: true,
        onSuccess() {
            announcementForm.reset()
        },
    })
}
</script>

<template>
    <div class="min-h-screen bg-[#f3f1ea] -mx-4 sm:-mx-6 lg:-mx-8">
        <div class="bg-[rgb(181_115_5)] text-white">
            <div class="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
                <p class="text-sm font-medium uppercase tracking-[0.2em] text-white/70">Today</p>
                <h1 class="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">Dashboard</h1>
                <p class="mt-2 max-w-2xl text-white/80">{{ dateLabel }}</p>
            </div>
        </div>

        <div class="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
            <div v-if="employee || myShift || attendance" class="grid gap-4 md:grid-cols-3">
                <Card>
                    <CardHeader class="pb-2">
                        <CardDescription>Your number</CardDescription>
                        <CardTitle class="font-mono tracking-wide">
                            {{ employee?.employee_number ?? "—" }}
                        </CardTitle>
                    </CardHeader>
                    <CardContent class="text-sm text-muted-foreground">
                        {{ employee ? `${employee.name} · ${employee.title}` : "No employee record linked to this account." }}
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader class="pb-2">
                        <CardDescription>Your shift</CardDescription>
                        <CardTitle>
                            {{ myShift ? `${myShift.start_label} – ${myShift.end_label}` : "Not scheduled" }}
                        </CardTitle>
                    </CardHeader>
                    <CardContent class="text-sm text-muted-foreground">
                        {{ myShift ? stationLabel[myShift.station] ?? myShift.station : "You are not on today's board." }}
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader class="pb-2">
                        <CardDescription>Time clock</CardDescription>
                        <CardTitle>
                            <span v-if="attendance?.status === 'timed_out'">Timed out</span>
                            <span v-else-if="attendance?.status === 'timed_in'">Timed in</span>
                            <span v-else>Not timed in</span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent class="flex items-center justify-between gap-3 text-sm text-muted-foreground">
                        <span>
                            <template v-if="attendance?.timed_in_label">In {{ attendance.timed_in_label }}</template>
                            <template v-if="attendance?.timed_out_label"> · Out {{ attendance.timed_out_label }}</template>
                            <template v-if="!attendance">Clock in when your shift starts.</template>
                        </span>
                        <Link :href="links.time_clock_path">
                            <Button size="sm" class="bg-[rgb(181_115_5)] hover:bg-[rgb(145_90_0)]">Clock</Button>
                        </Link>
                    </CardContent>
                </Card>
            </div>

            <div class="grid gap-6 lg:grid-cols-2">
                <Card>
                    <CardHeader>
                        <div class="flex items-center gap-2">
                            <CalendarDays class="h-5 w-5 text-[rgb(181_115_5)]" />
                            <div>
                                <CardTitle>Today's schedule</CardTitle>
                                <CardDescription>Who is on the floor today.</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent class="space-y-6">
                        <div v-for="group in groupedSchedule" :key="group.station">
                            <h3 class="mb-2 text-sm font-semibold text-gray-900">{{ group.label }}</h3>
                            <div v-if="group.shifts.length === 0" class="rounded-md border border-dashed bg-[#f7f5ef] px-3 py-4 text-sm text-muted-foreground">
                                No one scheduled.
                            </div>
                            <ul v-else class="divide-y rounded-md border bg-white">
                                <li
                                    v-for="shift in group.shifts"
                                    :key="shift.id"
                                    class="flex items-center justify-between gap-3 px-3 py-2.5"
                                    :class="employee?.id === shift.employee.id ? 'bg-[rgb(181_115_5)]/10' : ''"
                                >
                                    <div>
                                        <p class="text-sm font-medium text-gray-900">{{ shift.employee.name }}</p>
                                        <p class="text-xs text-muted-foreground">
                                            {{ shift.employee.employee_number }} · {{ shift.employee.title }}
                                        </p>
                                    </div>
                                    <p class="shrink-0 text-sm text-gray-700">{{ shift.start_label }} – {{ shift.end_label }}</p>
                                </li>
                            </ul>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <div class="flex items-center gap-2">
                            <Megaphone class="h-5 w-5 text-[rgb(181_115_5)]" />
                            <div>
                                <CardTitle>Announcements</CardTitle>
                                <CardDescription>Notes for the team.</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent class="space-y-4">
                        <form v-if="canPostAnnouncements" class="space-y-3 rounded-md border bg-[#f7f5ef] p-3" @submit.prevent="submitAnnouncement">
                            <Input v-model="announcementForm.title" placeholder="Title" />
                            <textarea
                                v-model="announcementForm.body"
                                rows="3"
                                placeholder="What should the team know?"
                                class="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            />
                            <p v-if="announcementForm.errors.title" class="text-sm text-destructive">
                                {{ announcementForm.errors.title }}
                            </p>
                            <p v-if="announcementForm.errors.body" class="text-sm text-destructive">
                                {{ announcementForm.errors.body }}
                            </p>
                            <Button type="submit" :disabled="announcementForm.processing" size="sm" class="bg-[rgb(181_115_5)] hover:bg-[rgb(145_90_0)]">
                                Post announcement
                            </Button>
                        </form>

                        <div v-if="announcements.length === 0" class="rounded-md border border-dashed px-3 py-8 text-center text-sm text-muted-foreground">
                            No announcements yet.
                        </div>
                        <ul v-else class="space-y-3">
                            <li v-for="announcement in announcements" :key="announcement.id" class="rounded-md border bg-white p-3">
                                <p class="font-medium text-gray-900">{{ announcement.title }}</p>
                                <p class="mt-1 whitespace-pre-wrap text-sm text-gray-700">{{ announcement.body }}</p>
                                <p class="mt-2 text-xs text-muted-foreground">
                                    {{ announcement.author }}
                                    <span v-if="announcement.published_label"> · {{ announcement.published_label }}</span>
                                </p>
                            </li>
                        </ul>
                    </CardContent>
                </Card>
            </div>
        </div>
    </div>
</template>
